import React, { FC, useState } from 'react';
import ReactDOM from 'react-dom';
import { Divider, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useHistory } from 'react-router';

import { GlobeIcon, UaeLogoutSvgIcon } from '~/img';
import { useLogout } from '~/controllers/auth';
import { useDirection, useSetLanguage } from '~/hooks/i18nHooks';
import paths from '~/pages/paths';

/**
 * UAE Self-Onboarding Portal — mobile hamburger menu.
 * Opens a full-height drawer with: Contact Us, Change Language, Logout,
 * and a copyright footer. Matches the Figma mobile design.
 * Intentionally separate from HeaderBurgerMenu to avoid affecting Agent/Sales portals.
 */
const UaeMobileMenu: FC = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('common');
  const dir = useDirection();
  const { handleLogout } = useLogout();
  const { language, setLanguage, languages, languageNames } = useSetLanguage();
  const history = useHistory();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleContactUs = () => {
    history.push(paths.ksa.contactUs);
    handleClose();
  };

  const handleLanguageChange = (lng: string) => {
    setLanguage(lng);
    handleClose();
  };

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogoutClick = async () => {
    handleClose();
    setIsLoggingOut(true);
    try {
      await handleLogout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      {/* Hamburger trigger — stays in header */}
      <HamburgerButton
        id="uaeMobileMenu-open"
        className="UaeMobileMenu-Trigger"
        onClick={handleOpen}
        aria-label={t('Menu')}
      >
        <HamburgerIcon />
      </HamburgerButton>

      {ReactDOM.createPortal(
        <>
          {/* Backdrop */}
          {open && <Backdrop onClick={handleClose} />}

          {/* Drawer */}
          <Drawer open={open} dir={dir} className="UaeMobileMenu-Drawer">
            {/* Header row */}
            <DrawerHeader dir={dir}>
              <CloseButton
                id="uaeMobileMenu-close"
                onClick={handleClose}
                aria-label={t('Close')}
              >
                <CloseIcon />
              </CloseButton>
            </DrawerHeader>

            <DrawerBody>
              {/* Contact Us */}
              <MenuItem id="uaeMobileMenu-contactUs" onClick={handleContactUs}>
                <MenuItemIcon>
                  <PhoneCallIcon />
                </MenuItemIcon>
                <MenuItemLabel>{t('Contact us')}</MenuItemLabel>
              </MenuItem>

              <StyledDivider />

              {/* Change Language */}
              <LanguageSection>
                <LanguageSectionHeader>
                  <MenuItemIcon>
                    <GlobeIcon />
                  </MenuItemIcon>
                  <MenuItemLabel>{t('Change Language')}</MenuItemLabel>
                </LanguageSectionHeader>
                <LanguageOptions>
                  {languages.map((lng) => (
                    <LanguageOption
                      key={lng}
                      id={`uaeMobileMenu-lang-${lng}`}
                      active={language === lng}
                      onClick={() => handleLanguageChange(lng)}
                    >
                      {languageNames[lng] || lng}
                    </LanguageOption>
                  ))}
                </LanguageOptions>
              </LanguageSection>

              <StyledDivider />

              {/* Logout */}
              <MenuItem id="uaeMobileMenu-logout" onClick={handleLogoutClick}>
                <MenuItemIcon>
                  <StyledLogoutIcon />
                </MenuItemIcon>
                <MenuItemLabel>{t('Logout')}</MenuItemLabel>
              </MenuItem>
            </DrawerBody>

            {/* Footer */}
            <DrawerFooter>
              <FooterText>
                {t('Copyright {{year}} Network International. All Rights Reserved.', {
                  year: new Date().getFullYear(),
                })}
              </FooterText>
            </DrawerFooter>
          </Drawer>
        </>,
        document.body
      )}

      {/* Logout loading overlay */}
      {isLoggingOut &&
        ReactDOM.createPortal(
          <LogoutLoadingOverlay>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 40, color: '#0060a8' }} spin />}
            />
          </LogoutLoadingOverlay>,
          document.body
        )}
    </>
  );
};

/* ─── Styled Components ─────────────────────────────────────── */

const HamburgerButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #1b1b20;
`;

/* Hamburger icon — exact SVG from design spec */
const HamburgerIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18" fill="none">
    <path
      d="M0.687503 16.6875L18.6875 16.6875M8.6875 8.6875L18.6875 8.6875M0.6875 0.6875L18.6875 0.687497"
      stroke="#070707"
      strokeWidth="1.375"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Close icon — exact SVG from design spec */
const CloseIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path
      d="M8 24L24 8M8 8L24 24"
      stroke="#070707"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Phone call icon — exact SVG from design spec (Contact Us) */
const PhoneCallIcon: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M4.79292 13C3.41847 10.6714 2.51715 7.9549 2.16585 4.92733C2.06116 4.02505 2.00881 3.57391 2.18915 3.13568C2.33648 2.77765 2.65747 2.41725 2.99614 2.22964C3.41066 2 3.91822 2 4.93333 2H5.20972C6.02862 2 6.43806 2 6.7787 2.14312C7.07925 2.26939 7.3407 2.47352 7.5361 2.73447C7.75756 3.03023 7.85686 3.42745 8.05547 4.2219L8.27323 5.0929C8.53948 6.1579 8.6726 6.69041 8.65255 7.21298C8.63478 7.67576 8.52951 8.13098 8.3423 8.55458C8.1309 9.0329 7.77751 9.4529 7.07073 10.2929L4.79292 13ZM4.79292 13C6.24802 15.4652 8.23342 17.4956 10.6579 19M10.6579 19C13.061 20.4912 15.8956 21.4655 19.0727 21.8341C19.9749 21.9388 20.4261 21.9912 20.8643 21.8109C21.2224 21.6635 21.5827 21.3425 21.7704 21.0039C22 20.5893 22 20.0818 22 19.0667C22 18.9743 22 18.8821 22 18.7902C22 17.9713 22 17.5619 21.8569 17.2213C21.7306 16.9207 21.5265 16.6593 21.2655 16.4639C20.9698 16.2424 20.5725 16.1431 19.7781 15.9445L18.7062 15.6766C17.712 15.428 17.2149 15.3037 16.7239 15.3142C16.2889 15.3235 15.859 15.4101 15.4543 15.5701C14.9976 15.7506 14.5874 16.0577 13.7671 16.6719L10.6579 19Z"
      stroke="#4A4A4A"
      strokeWidth="1.375"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1500;
`;

interface DrawerProps {
  open: boolean;
  dir?: string;
}

const Drawer = styled.div<DrawerProps>`
  position: fixed;
  top: 0;
  right: ${({ dir }) => (dir === 'rtl' ? 'auto' : '0')};
  left: ${({ dir }) => (dir === 'rtl' ? '0' : 'auto')};
  width: min(360px, 90vw);
  height: 100dvh;
  background: #f5f9fc;
  border-radius: 0;
  z-index: 1600;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px -12px rgba(16, 24, 40, 0.18);
  transform: ${({ open, dir }) =>
    open
      ? 'translateX(0)'
      : dir === 'rtl'
        ? 'translateX(-100%)'
        : 'translateX(100%)'};
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
`;

interface DrawerHeaderProps {
  dir?: string;
}

const DrawerHeader = styled.div<DrawerHeaderProps>`
  display: flex;
  align-items: center;
  /* RTL: close button on left side; LTR: close button on right side */
  justify-content: ${({ dir }) => (dir === 'rtl' ? 'flex-end' : 'flex-end')};
  padding: 20px 24px 16px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: #1b1b20;
  display: flex;
  align-items: center;
  padding: 4px;
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 32px;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 0;
  cursor: pointer;
  color: #1b1b20;

  &:hover {
    opacity: 0.7;
  }
`;

const MenuItemIcon = styled.span`
  font-size: 24px;
  display: flex;
  align-items: center;
  color: #1b1b20;
`;

const MenuItemLabel = styled.span`
  font-size: 14px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-style: normal;
  line-height: 24px;
  color: #070707;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const LanguageSection = styled.div`
  padding: 18px 0 8px;
`;

const LanguageSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
`;

const LanguageOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-inline-start: 40px;
`;

interface LanguageOptionProps {
  active?: boolean;
}

const LanguageOption = styled.div<LanguageOptionProps>`
  padding: 10px 0;
  font-size: 14px;
  font-family: Inter, sans-serif;
  font-style: normal;
  font-weight: ${({ active }) => (active ? '600' : '500')};
  line-height: 24px;
  cursor: pointer;
  color: ${({ active }) => (active ? '#0060a8' : '#070707')};

  &:hover {
    color: #0060a8;
  }
`;

const DrawerFooter = styled.div`
  padding: 32px 16px;
  text-align: center;
`;

const FooterText = styled.span`
  display: block;
  align-self: stretch;
  color: #1c2027;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.14px;
`;

const LogoutLoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledLogoutIcon = styled(UaeLogoutSvgIcon)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  aspect-ratio: 1 / 1;
`;

export default UaeMobileMenu;
